import { useState, useRef } from 'react';
import { read_cookie } from "sfcookies"
import { hostUrl, UseUrlManagerCleanUrl } from "../../../../utils/url-manager";
import {
    fetchOperatorConnection,
    operatorSocketUrl,
    operatorSocketProtocols,
    OperatorConnection
} from "../../../../../providers/operatorConnection";

type UseNokTesterReturnProps = {
    isTestRunning: boolean,
    beginTest: (dataProvider: any,testPayload: any) => Promise<any>,
    testErrored: string|undefined,
    testResults: any,
    disconnectOperator: () => void
}



export const useNokTester: () => UseNokTesterReturnProps = (): UseNokTesterReturnProps => {
    const [isTestRunning, setIsTestRunning] = useState<boolean>(false);
    const [testErrored, setTestErrored] = useState<string|undefined>(undefined);
    const [operator, setOperator] = useState<any|undefined>(undefined);
    const [testResults, setTestResults] = useState<any|undefined>(undefined);
    const [connectorId, setConnectorId] = useState<string|undefined>(undefined);
    // Refs, not plain locals: a `let` in the hook body is re-created on every render, so the
    // socket opened by one render is invisible to every later one. That made
    // disconnectOperator() a silent no-op whenever it was called from a subsequent render
    // (the beforeunload handler, and now the Cancel button).
    const websocket = useRef<WebSocket|undefined>(undefined);
    const safeClose = useRef<boolean>(false);

    async function connectorToOperator(dataProvider: any, testPayload?: any): Promise<void> {
        return await loadOperator()
            .then( async (data: any): Promise<void> => {
                if (data?.status === 'success') {
                    const oper: any = data?.data;
                    let connector: string = createRandomString(10);

                    // HQAPI-76. `oper.token` was the Operator's own standing secret, served on
                    // the operators collection and put in the query string here. HQ-API answers
                    // a host and a signed ticket instead, and the ticket rides in the
                    // subprotocol so it is not written to a proxy log on the way past.
                    let connection: OperatorConnection;
                    try {
                        connection = await fetchOperatorConnection(oper.id);
                    } catch (error: any) {
                        setTestErrored('Could not open a connection to the Operator.');
                        setIsTestRunning(false);
                        return;
                    }
                    let socketUrl: string = operatorSocketUrl(
                        connection,
                        '/operator/noks/tester/ws',
                        { connector: connector }
                    );

                    safeClose.current = false;

                    try {
                        websocket.current = new WebSocket(
                            socketUrl,
                            operatorSocketProtocols(connection)
                        );
                    } catch (error: any) {
                        setTestErrored('Could not open a connection to the Operator.');
                        setIsTestRunning(false);
                        return;
                    }

                    websocket.current.onopen = async function (event: Event): Promise<void> {
                        setConnectorId(connector);
                        await runTest(dataProvider, testPayload, connector);
                    }
                    websocket.current.onmessage = async function (event: MessageEvent): Promise<void> {
                        const response = JSON.parse(event.data);
                        setTestResults(response);
                        setIsTestRunning(false);
                        disconnectOperator();
                    }
                    websocket.current.onerror = async function (event: Event): Promise<void> {
                        setTestErrored('Operator not loaded');
                        setIsTestRunning(false);
                    }
                    websocket.current.onclose = async function (event: CloseEvent): Promise<void> {
                        if (!safeClose.current) {
                            setTestErrored('Operator connection closed');
                        }
                        websocket.current = undefined;
                        setIsTestRunning(false);
                    }
                } else if (data !== undefined && data?.status !== 'error') {
                    // loadOperator only reports status 'error' itself; anything else that is
                    // not 'success' would otherwise leave the spinner running with no message.
                    setTestErrored('The Operator did not return a valid connection.');
                    setIsTestRunning(false);
                }
            } );
    }

    function disconnectOperator(): void {
        const socket: WebSocket|undefined = websocket.current;

        if (socket !== undefined) {
            safeClose.current = true;
            // send() throws if the socket is still CONNECTING or already closed, which would
            // leave the caller (the Cancel button) stuck mid-teardown.
            if (socket.readyState === WebSocket.OPEN) {
                socket.send('/stop');
            }
            socket.close();
            websocket.current = undefined;
        }

        setIsTestRunning(false);
    }

    async function beginTest(dataProvider: any, testPayload?: any): Promise<any> {
        setTestErrored(undefined);
        setTestResults(undefined);
        setIsTestRunning(true);
        if (websocket.current === undefined) {
            await connectorToOperator(dataProvider, testPayload)
        } else {
            await runTest(dataProvider, testPayload);
        }

    }

    async function runTest(dataProvider: any,  testPayload: any, id?: string, ): Promise<any> {
        let noks = testPayload?.noks
        let updatedNoks = [];
        for (const nok of noks) {
            if (nok?.beforeNokExecution !== undefined) {

                let beforeNoks: any = nok?.beforeNokExecution;
                let newBeforeNoks = [];
                for (let i: number = 0; i < beforeNoks.length; i++) {
                    let item = beforeNoks[i];
                    const results = await dataProvider.getOne('noks', { id: item.nok })
                    if (results?.data) {
                       item['configuration'] = results.data
                        newBeforeNoks.push(item);
                    }
                }
                if (newBeforeNoks.length > 0) {
                    nok.beforeNokExecution = newBeforeNoks;
                }

            }
            if (nok?.afterNokExecution !== undefined) {

                let afterNok: any = nok?.afterNokExecution;
                let newAfterNoks = [];
                for (let i: number = 0; i < afterNok.length; i++) {
                    let item = afterNok[i];
                    const results = await dataProvider.getOne('noks', { id: item.nok })
                    if (results?.data) {
                        item['configuration'] = results.data
                        newAfterNoks.push(item);
                    }
                }
                if (newAfterNoks.length > 0) {
                    nok.afterNokExecution = newAfterNoks;
                }
            }
        }

        let payload: { payload: string, connectorId: string } = {
            payload: JSON.stringify(testPayload),
            connectorId: id ?? connectorId
        }

        if (websocket.current === undefined || websocket.current.readyState !== WebSocket.OPEN) {
            setTestErrored('The Operator connection is not open.');
            setIsTestRunning(false);
            return;
        }

        websocket.current.send(JSON.stringify(payload));

    }

    async function loadOperator(): Promise<any> {

        const token: string = read_cookie('auth');
        const request: Request = new Request(UseUrlManagerCleanUrl({ url: hostUrl + 'hq/nok/tester/operator' }), {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token': token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<any> => {
                return response.json()
            } )
            .then( (data: any): Promise<any> => {
                if (data?.status === 'success') {
                    setOperator(data?.data);
                }
                if (data?.status === 'error') {
                    setTestErrored(data?.message);
                    setIsTestRunning(false);
                }

                return data;
            })
            .catch( (error: Error): void => {
                // Without this the rejection was only logged, connectorToOperator then read
                // status off undefined, and the spinner ran forever with no explanation.
                setTestErrored('Could not reach the Operator. ' + (error?.message ?? ''));
                setIsTestRunning(false);
                return undefined;
            });
    }

    function createRandomString(length: number): string {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let result = "";
        for (let i: number = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    }

    return { isTestRunning, beginTest, testErrored, testResults, disconnectOperator };
}